import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { invalidateFor } from '@/lib/cache-registry';
import { isProjectLockedError, toastProjectLocked } from '@/lib/db-errors';
import type { UseMyTasksResult } from './use-my-tasks';

// Helper: optimistically update task_status in the cache
function optimisticStatusUpdate(queryClient: ReturnType<typeof useQueryClient>, assignmentId: string, newStatus: string) {
  queryClient.setQueriesData<UseMyTasksResult>(
    { queryKey: ['my-tasks'] },
    (old) => {
      if (!old) return old;
      return {
        ...old,
        tasks: old.tasks.map((t) =>
          t.id === assignmentId ? { ...t, task_status: newStatus } : t
        ),
        taskItems: old.taskItems.map((t) =>
          t.id === assignmentId ? { ...t, status: newStatus } : t
        ),
      };
    }
  );
}

// VIB-1184 D15: task-status-events (task_updated/completed/approved/rejected)
// logges autoritativt av DB-triggeren log_task_status_change (project_events).
// Frontend-duppen (logTaskStatusEvent) er fjernet.

export function useStartTask() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (assignmentId: string) => {
      const { error } = await supabase
        .from('budget_line_assignments')
        .update({ task_status: 'in_progress' })
        .eq('id', assignmentId);
      if (error) throw error;
    },
    onMutate: async (assignmentId) => {
      await queryClient.cancelQueries({ queryKey: ['my-tasks'] });
      optimisticStatusUpdate(queryClient, assignmentId, 'in_progress');
    },
    onSuccess: (_d, assignmentId) => {
    },
    onError: async (err, assignmentId) => {
      if (isProjectLockedError(err)) {
        const ctx = await fetchTaskContext(assignmentId);
        toastProjectLocked({ navigate, projectId: ctx?.project_id });
        return;
      }
      toast.error('Kunne ikke starte oppgaven');
    },
    onSettled: async (_data, _err, assignmentId) => {
      const ctx = await fetchTaskContext(assignmentId);
      await invalidateFor(queryClient, 'budget_line_assignments', {
        projectId: ctx?.project_id,
      });
    },
  });
}

/**
 * Henter prosjekt-id og oppgavebeskrivelse for et assignment. Brukes til
 * deep-link ved project-locked-feil og til målrettet cache-invalidering.
 */
type AssignmentWithProject = {
  budget_lines: {
    description: string | null;
    projects: { id: string; name: string } | null;
  } | null;
};

async function fetchTaskContext(assignmentId: string) {
  const { data } = await supabase
    .from('budget_line_assignments')
    .select('budget_lines(description, projects(id, name))')
    .eq('id', assignmentId)
    .maybeSingle();
  if (!data) return null;
  const typed = data as unknown as AssignmentWithProject;
  const project = typed.budget_lines?.projects;
  if (!project) return null;
  return {
    project_id: project.id,
    project_name: project.name,
    description: typed.budget_lines?.description ?? '',
  };
}

export function useCompleteTask() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (assignmentId: string) => {
      const { error } = await supabase
        .from('budget_line_assignments')
        // MEDIUM-1: sett completed_at ved fullføring (autoritativ for måned-
        // attribusjon i faktureringsgrad). DB-trigger er backstop for andre stier.
        .update({ task_status: 'completed', completed_at: new Date().toISOString() })
        .eq('id', assignmentId);
      if (error) throw error;
    },
    onMutate: async (assignmentId) => {
      await queryClient.cancelQueries({ queryKey: ['my-tasks'] });
      optimisticStatusUpdate(queryClient, assignmentId, 'completed');
    },
    onSuccess: (_ctx, assignmentId) => {
    },
    onError: async (err, assignmentId) => {
      if (isProjectLockedError(err)) {
        const ctx = await fetchTaskContext(assignmentId);
        toastProjectLocked({ navigate, projectId: ctx?.project_id });
        return;
      }
      toast.error('Kunne ikke fullføre oppgaven');
    },
    onSettled: async (_data, _err, assignmentId) => {
      const ctx = await fetchTaskContext(assignmentId);
      await invalidateFor(queryClient, 'budget_line_assignments', {
        projectId: ctx?.project_id,
      });
    },
  });
}

// VIB-956: Prosjektleder kan markere sine EGNE oppgaver som «Fullført» rett til
// approved, uten godkjenningsrunde (sirkulær selv-godkjenning er rart). Setter
// completed_at (autoritativ for månedsattribusjon) + approved_by=self + approved_at.
// Ingen notifikasjon (selv-godkjenning). Gates klient-side på task.is_project_lead.
export function useSelfCompleteTask() {
  const queryClient = useQueryClient();
  const { activeUser } = useImpersonation();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (assignmentId: string) => {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from('budget_line_assignments')
        .update({
          task_status: 'approved',
          completed_at: now,
          approved_by: activeUser?.id ?? null,
          approved_at: now,
        })
        .eq('id', assignmentId);
      if (error) throw error;
      return await fetchTaskContext(assignmentId);
    },
    onMutate: async (assignmentId) => {
      await queryClient.cancelQueries({ queryKey: ['my-tasks'] });
      optimisticStatusUpdate(queryClient, assignmentId, 'approved');
    },
    onSuccess: (_ctx, assignmentId) => {
    },
    onError: async (err, assignmentId) => {
      if (isProjectLockedError(err)) {
        const ctx = await fetchTaskContext(assignmentId);
        toastProjectLocked({ navigate, projectId: ctx?.project_id });
        return;
      }
      toast.error('Kunne ikke fullføre oppgaven');
    },
    onSettled: async (_data, _err, assignmentId) => {
      const ctx = await fetchTaskContext(assignmentId);
      await invalidateFor(queryClient, 'budget_line_assignments', {
        projectId: ctx?.project_id,
      });
    },
  });
}

export function useUndoCompleteTask() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (assignmentId: string) => {
      const { error } = await supabase
        .from('budget_line_assignments')
        .update({ task_status: 'in_progress' })
        .eq('id', assignmentId);
      if (error) throw error;
    },
    onMutate: async (assignmentId) => {
      await queryClient.cancelQueries({ queryKey: ['my-tasks'] });
      optimisticStatusUpdate(queryClient, assignmentId, 'in_progress');
    },
    onSuccess: (_d, assignmentId) => {
    },
    onError: async (err, assignmentId) => {
      if (isProjectLockedError(err)) {
        const ctx = await fetchTaskContext(assignmentId);
        toastProjectLocked({ navigate, projectId: ctx?.project_id });
        return;
      }
      toast.error('Kunne ikke angre fullføring');
    },
    onSettled: async (_data, _err, assignmentId) => {
      const ctx = await fetchTaskContext(assignmentId);
      await invalidateFor(queryClient, 'budget_line_assignments', {
        projectId: ctx?.project_id,
      });
    },
  });
}

export function useReopenTask() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (assignmentId: string) => {
      const { error } = await supabase
        .from('budget_line_assignments')
        .update({ task_status: 'in_progress', rejection_reason: null })
        .eq('id', assignmentId);
      if (error) throw error;
    },
    onMutate: async (assignmentId) => {
      await queryClient.cancelQueries({ queryKey: ['my-tasks'] });
      optimisticStatusUpdate(queryClient, assignmentId, 'in_progress');
    },
    onSuccess: (_d, assignmentId) => {
    },
    onError: async (err, assignmentId) => {
      if (isProjectLockedError(err)) {
        const ctx = await fetchTaskContext(assignmentId);
        toastProjectLocked({ navigate, projectId: ctx?.project_id });
        return;
      }
      toast.error('Kunne ikke åpne oppgaven på nytt');
    },
    onSettled: async (_data, _err, assignmentId) => {
      const ctx = await fetchTaskContext(assignmentId);
      await invalidateFor(queryClient, 'budget_line_assignments', {
        projectId: ctx?.project_id,
      });
    },
  });
}

export function useApproveTask() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async ({ assignmentId, approvedBy }: { assignmentId: string; approvedBy?: string }) => {
      const { error } = await supabase
        .from('budget_line_assignments')
        .update({ task_status: 'approved', approved_by: approvedBy ?? null, approved_at: new Date().toISOString() })
        .eq('id', assignmentId);
      if (error) throw error;
    },
    onMutate: async ({ assignmentId }) => {
      await queryClient.cancelQueries({ queryKey: ['my-tasks'] });
      optimisticStatusUpdate(queryClient, assignmentId, 'approved');
    },
    onSuccess: (_data, { assignmentId }) => {
    },
    onError: async (err, { assignmentId }) => {
      if (isProjectLockedError(err)) {
        const ctx = await fetchTaskContext(assignmentId);
        toastProjectLocked({ navigate, projectId: ctx?.project_id });
        return;
      }
      toast.error('Kunne ikke godkjenne oppgaven');
    },
    onSettled: async (_data, _err, { assignmentId }) => {
      const ctx = await fetchTaskContext(assignmentId);
      await invalidateFor(queryClient, 'budget_line_assignments', {
        projectId: ctx?.project_id,
      });
    },
  });
}

export function useRejectTask() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async ({ assignmentId, reason }: { assignmentId: string; reason: string }) => {
      const { error } = await supabase
        .from('budget_line_assignments')
        .update({ task_status: 'rejected', rejection_reason: reason })
        .eq('id', assignmentId);
      if (error) throw error;
    },
    onMutate: async ({ assignmentId, reason }) => {
      await queryClient.cancelQueries({ queryKey: ['my-tasks'] });
      queryClient.setQueriesData<UseMyTasksResult>(
        { queryKey: ['my-tasks'] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            tasks: old.tasks.map((t) =>
              t.id === assignmentId ? { ...t, task_status: 'rejected', rejection_reason: reason } : t
            ),
            taskItems: old.taskItems.map((t) =>
              t.id === assignmentId ? { ...t, status: 'rejected' } : t
            ),
          };
        }
      );
    },
    onSuccess: (_data, { assignmentId, reason }) => {
    },
    onError: async (err, { assignmentId }) => {
      if (isProjectLockedError(err)) {
        const ctx = await fetchTaskContext(assignmentId);
        toastProjectLocked({ navigate, projectId: ctx?.project_id });
        return;
      }
      toast.error('Kunne ikke avvise oppgaven');
    },
    onSettled: async (_data, _err, { assignmentId }) => {
      const ctx = await fetchTaskContext(assignmentId);
      await invalidateFor(queryClient, 'budget_line_assignments', {
        projectId: ctx?.project_id,
      });
    },
  });
}

export function useUpdateAssignment() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async ({ assignmentId, updates }: { assignmentId: string; updates: Record<string, unknown> }) => {
      const { error } = await supabase
        .from('budget_line_assignments')
        .update(updates)
        .eq('id', assignmentId);
      if (error) throw error;
    },
    onError: async (err, { assignmentId }) => {
      if (isProjectLockedError(err)) {
        const ctx = await fetchTaskContext(assignmentId);
        toastProjectLocked({ navigate, projectId: ctx?.project_id });
        return;
      }
      toast.error('Kunne ikke oppdatere oppgaven');
    },
    onSettled: async (_data, _err, { assignmentId }) => {
      const ctx = await fetchTaskContext(assignmentId);
      await invalidateFor(queryClient, 'budget_line_assignments', {
        projectId: ctx?.project_id,
      });
    },
  });
}
